import { ChangeEvent, PropsWithChildren, ReactElement } from "react";
import { className } from "../../../../shared/helpers";
import { CalendarDate } from "../../../../services/datepicker/interfaces/calendar-date";
import DatePickerService from "../../../../services/datepicker/datepicker-service";
import CustomSelect from "../../../Shared/CustomSelect";
import styles from "./Calendar.module.scss";

interface CalendarProps
{
    title?: string;
    currentDate: CalendarDate;
    selectedDate: CalendarDate;
    datepickerService: DatePickerService;
    updateDate: (date: CalendarDate) => void;
    selectDate: (date: CalendarDate) => void;
    close: () => void;
}

const Calendar = (props: PropsWithChildren<CalendarProps>): ReactElement =>
{
    const { currentDate, datepickerService } = props;
    const firstDay = datepickerService.getFirstDayOfAMonth(currentDate.month, currentDate.year);

    function checkIfSelected(selectedDay: number): boolean
    {
        if (!props.selectedDate) return false;
        const currentDate = new Date(props.currentDate.year, props.currentDate.month, props.currentDate.day);
        const selectedDate = new Date(props.selectedDate.year, props.selectedDate.month, selectedDay);
        return currentDate.toDateString() === selectedDate.toDateString();
    }

    function getArrayOfDays(): number[]
    {
        return [...Array(datepickerService.getNumberOfDaysInAMonth(currentDate.month, currentDate.year)).keys()];
    }

    function updateYear(e: ChangeEvent): void
    {
        const target = e.target as HTMLInputElement;
        const min = parseInt(process.env.REACT_APP_DATEPICKER_MIN! ?? 1950);
        let year =  parseInt(target.value);

        if(target.value.length > 4 || parseInt(target.value) > 2500)
        {
            year = 2500;
        }
        else if(target.value.length < 1 )
        {
            year = min;
        }


        if (!year) return;

        const updatedDate = datepickerService.goToMonth(undefined, year);
        props.updateDate(updatedDate);
    }

    return (
        <>
            <div className={styles.calendarOverlay} onClick={props.close} />

            <div className={styles.calendar}>
                {props.title && <h4>{props.title}</h4>}

                <div className={styles.calendarInner}>
                    {/* Header */}
                    <header className={styles.calendarHeader}>
                        <button onClick={() => props.updateDate(datepickerService.goToPreviousMonth())}>
                            <i className="material-icons">chevron_left</i>
                        </button>

                        <CustomSelect label={datepickerService.localeMonths[props.currentDate.month]}>
                            {datepickerService.localeMonths.map((month: string, i: number) => (
                                <button key={month} onClick={() => props.updateDate(datepickerService.goToMonth(i))}>{month}</button>
                            ))}
                        </CustomSelect>

                        <input type="number" value={currentDate.year} onChange={updateYear} />

                        <button onClick={() => props.updateDate(datepickerService.goToNextMonth())}>
                            <i className="material-icons">chevron_right</i>
                        </button>
                    </header>

                    {/* Body */}
                    <div
                        className={styles.calendarBody}
                        style={{ ["--firstDay" as string]: firstDay === 0 ? 7 : firstDay }}
                    >
                        <ul className={styles.weekDays}>
                            {datepickerService.localeWeekDays.map((day: string) => (
                                <li key={day}>{day[0]}</li>
                            ))}
                        </ul>

                        <div className={styles.days}>
                            {getArrayOfDays().map((day: number) => (
                                <button
                                    key={`${currentDate.month}_day_${day}`}
                                    data-day={++day}
                                    onClick={() => props.selectDate(datepickerService.setCurrentDate({ ...currentDate, day }))}
                                    {...className(styles.day, {
                                        [styles.isToday]: datepickerService.checkIfToday({ ...currentDate, day }),
                                        [styles.isSelected]: checkIfSelected(day)
                                    })}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {props.children}
            </div>
        </>
    );
};

export default Calendar;
